import { formatMoney as formatHorizonMoney } from '@theme/money-formatting';

function createFormatter(currency, locale) {
  try {
    return new Intl.NumberFormat(locale, {
      style: 'currency',
      currency: String(currency || 'USD').toUpperCase(),
    });
  } catch (_) {
    return null;
  }
}

export function getShopifyMoneyDivisor(
  currency = 'USD',
  locale = undefined,
) {
  const formatter = createFormatter(currency, locale);
  if (!formatter) return 100;
  const precision = formatter.resolvedOptions().maximumFractionDigits;
  return precision === 0 ? 100 : 10 ** precision;
}

/**
 * Format monetary integers returned by Shopify Liquid and Ajax APIs.
 *
 * Horizon's shared money helper intentionally works in each currency's native
 * minor unit because its facet inputs are parsed from localized display text.
 * Shopify's rendered/Ajax integers differ for zero-decimal currencies: JPY and
 * KRW append two zeroes. Pogi commerce components consume those Shopify
 * integers directly, so they need this adapter rather than the facet helper.
 *
 * @param {number|string} shopifyAmount
 * @param {string} _moneyFormat Retained for a call signature compatible with Horizon.
 * @param {string} currency
 * @param {string|undefined} locale
 * @returns {string}
 */
export function formatShopifyMoney(
  shopifyAmount,
  _moneyFormat = '{{amount}}',
  currency = 'USD',
  locale = undefined,
) {
  const formatter = createFormatter(currency, locale);
  if (!formatter) {
    return formatHorizonMoney(
      Number(shopifyAmount) || 0,
      '{{amount}}',
      'USD',
    );
  }
  const divisor = getShopifyMoneyDivisor(currency, locale);
  return formatter.format((Number(shopifyAmount) || 0) / divisor);
}

export function formatShopifyWholeMoney(
  shopifyAmount,
  moneyFormat = '{{amount_no_decimals}}',
  currency = 'USD',
  locale = undefined,
) {
  const formatter = createFormatter(currency, locale);
  if (!formatter) {
    return formatHorizonMoney(
      Number(shopifyAmount) || 0,
      '{{amount_no_decimals}}',
      'USD',
    );
  }
  return new Intl.NumberFormat(locale, {
    style: 'currency',
    currency: String(currency || 'USD').toUpperCase(),
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format((Number(shopifyAmount) || 0) / getShopifyMoneyDivisor(currency, locale));
}

/**
 * Formats one unit of a Shopify presentment-money amount without assuming the
 * currency has two fraction digits. Shopify still serializes JPY/KRW amounts
 * with two trailing zeroes, so divide by the Shopify divisor before the unit
 * count (rather than rounding the integer first).
 */
export function formatShopifyUnitMoney(
  shopifyAmount,
  unitCount,
  currency = 'USD',
  locale = undefined,
) {
  const amount = Number(shopifyAmount);
  const count = Number(unitCount);
  const formatter = createFormatter(currency, locale);
  if (!formatter || !Number.isFinite(amount) || !Number.isFinite(count) || count <= 0) {
    return '';
  }

  const unitAmount = amount / getShopifyMoneyDivisor(currency, locale) / count;
  const digits = formatter.resolvedOptions().maximumFractionDigits;
  // Whole-yen rounding can turn ¥12.5 into ¥13. Keep a tenth when the
  // currency's normal zero-decimal display would lose a material fraction.
  const needsTenths = digits === 0 && Math.abs(unitAmount - Math.round(unitAmount)) >= 0.05;
  const options = needsTenths
    ? {
        style: 'currency',
        currency: String(currency || 'USD').toUpperCase(),
        minimumFractionDigits: 1,
        maximumFractionDigits: 1,
      }
    : {
        style: 'currency',
        currency: String(currency || 'USD').toUpperCase(),
      };
  try {
    return new Intl.NumberFormat(locale, options).format(unitAmount);
  } catch (_) {
    return '';
  }
}

/**
 * Selects the exact amount that owns a PDP option card. Subscription mode is
 * valid only when Shopify supplied the matching allocation for that variant.
 */
export function resolveShopifyPdpPrice(variant, allocation, subscribe = false) {
  if (!variant || !Number.isSafeInteger(variant.price) || variant.price < 0) return null;
  if (!subscribe) {
    return { amount: variant.price, money: String(variant.money || ''), subscription: false };
  }
  if (!allocation || !Number.isSafeInteger(allocation.price) || allocation.price < 0) return null;
  return { amount: allocation.price, money: String(allocation.money || ''), subscription: true };
}

export function calculateShopifyUnitSavings(
  referenceAmount,
  referenceCount,
  amount,
  unitCount,
) {
  const reference = Number(referenceAmount);
  const referenceUnits = Number(referenceCount);
  const current = Number(amount);
  const units = Number(unitCount);
  if (
    !Number.isFinite(reference)
    || !Number.isFinite(referenceUnits)
    || !Number.isFinite(current)
    || !Number.isFinite(units)
    || referenceUnits <= 0
    || units <= 0
  ) return 0;
  return Math.max(0, Math.round((reference / referenceUnits) * units - current));
}
